import * as React from 'react';
import { observer, Observer } from 'mobx-react-lite';
import { debounce } from 'lodash';
import moment, { Moment } from 'moment';
import { isArchiveSortField, SessionsVisualStore } from '../../stores';
import type { ArchiveSortField } from '../../stores';
import { ApplicationSession, SessionFilter } from '../../../common/services/types';
import { Tooltip, Table, Card, Select, DatePicker, Button, Spin } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import { SorterResult, TablePaginationConfig } from 'antd/lib/table/interface';
import { generateDocumentIcon } from '../../../common/components/DocumentIconRenderer';
import Search from 'antd/lib/input/Search';
import { Utils } from '../../../common/misc/Utils';
import { hasPermission, HasPermission } from '../../../authorization/components/HasPermission';
import { AppPermissions } from '../../../authorization/Permissions';
import authContext from '../../../authorization/AuthContext';
import { useNavigate } from 'react-router';
import { CompletedSessionIcon } from '../../../common/components';
import './SessionsList.less';

type Props = {
    store: SessionsVisualStore;
};

export const SessionsList: React.FC<Props> = ({ store }) => {
    const navigate = useNavigate();
    const authCtx = React.useContext(authContext);
    React.useEffect(() => {
        const loadForCurrentUserOnly =
            hasPermission(authCtx.permissions, AppPermissions.CanSeeAnalysisPagePersonal) &&
            !hasPermission(authCtx.permissions, AppPermissions.CanSeeAnalysisPageAll);

        store.setLoadForCurrentUserOnly(loadForCurrentUserOnly);
        store.loadArchiveSessions();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [store]);

    const generateStatusIcon = (session: ApplicationSession) => {
        if (session.error) {
            return (
                <Tooltip title={session.error}>
                    <span
                        data-id-name="Status"
                        className="closed-session-status"
                        style={{ background: '#F5222D', fontSize: 11 }}
                    >
                        Failed
                    </span>
                </Tooltip>
            );
        }
        switch (session.state) {
            case 'Finished':
                return <CompletedSessionIcon session={session} />;
            case 'Archived':
                return (
                    <span
                        data-id-name="Status"
                        className="closed-session-status"
                        style={{ background: '#FA8C16', fontSize: 11 }}
                    >
                        Please review
                    </span>
                );
            default:
                return;
        }
    };

    const handleReloadClick = (session: ApplicationSession) => {
        navigate('/analysis');
        store.startSessionFromExisting(session);
    };

    const generateActionButtons = (session: ApplicationSession) => {
        return (
            <div className="closed-session-operation">
                <HasPermission permissionClaim={AppPermissions.CanManageSessionsAndDocuments}>
                    {session.isReloadable !== false ? (
                        <Tooltip title="Start from scratch">
                            <i className={'alpha-icon xs reload'} onClick={() => handleReloadClick(session)} />
                        </Tooltip>
                    ) : null}
                </HasPermission>

                <Tooltip title="Switch to Analysis form">
                    <i className={'alpha-icon xs session-switch'} onClick={() => switchToSession(session)} />
                </Tooltip>
            </div>
        );
    };

    const formatDate = (date: string | null) => {
        if (date) {
            return Intl.DateTimeFormat('en-Gb', {
                year: 'numeric',
                month: 'long',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit'
            }).format(new Date(date));
        } else {
            return '';
        }
    };

    const switchToSession = (session: ApplicationSession) => {
        store.switchToSession(session);
    };

    const mapStoredDateToPickerDate = (value?: string) => {
        if (!value) {
            return null;
        }

        const dateTime = moment.utc(value);

        return dateTime.isValid() ? dateTime : null;
    };

    const mapPickerDateToUtcIso = (date: Moment, endOfDay: boolean) => {
        if (!date.isValid()) {
            return undefined;
        }

        const utcDate = moment.utc([date.year(), date.month(), date.date()]);

        return (endOfDay ? utcDate.endOf('day') : utcDate.startOf('day')).toISOString();
    };

    const getDatePickerValue = (): [Moment | null, Moment | null] | null => {
        const { dateFrom, dateTo } = store.archiveSessionsParams.filter;

        if (!dateFrom && !dateTo) {
            return null;
        }

        return [mapStoredDateToPickerDate(dateFrom), mapStoredDateToPickerDate(dateTo)];
    };

    const getSorterOrder = (columnKey: ArchiveSortField) => {
        const { orderByField, orderByDirection } = store.archiveSessionsParams.filter;

        if (orderByField !== columnKey) {
            return;
        }

        return orderByDirection === 'ASC' ? 'ascend' : 'descend';
    };

    const columns: ColumnsType<ApplicationSession> = [
        {
            key: 'packageName',
            title: 'Document',
            sorter: true,
            sortOrder: getSorterOrder('packageName'),
            render: (a: ApplicationSession) => (
                <div className="label-with-icon archive-doc-name">
                    {generateDocumentIcon(a.packageName, !!a.packageSetId)}
                    <Tooltip title={a.packageName}>
                        <span data-id-name="Package">{a.packageName}</span>
                    </Tooltip>
                </div>
            )
        },
        {
            key: 'applicationName',
            title: 'Application',
            sorter: true,
            sortOrder: getSorterOrder('applicationName'),
            render: (a: ApplicationSession) => <span data-id-name="Application">{a.applicationName}</span>
        },
        {
            key: 'projectName',
            title: 'Project',
            sorter: true,
            sortOrder: getSorterOrder('projectName'),
            render: (a: ApplicationSession) => <span data-id-name="Project">{a.projectName}</span>
        },
        {
            title: 'Created By',
            render: (a: ApplicationSession) => <span data-id-name="User">{store.allUsers[a.userId!]}</span>
        },
        {
            key: 'created',
            title: 'Created',
            sorter: true,
            sortOrder: getSorterOrder('created'),
            // eslint-disable-next-line max-len
            render: (a: ApplicationSession) => (
                <span data-timestamp={Utils.dateStringToIso(formatDate(a.created))} data-id-name="Created">
                    {Utils.formatDateStringShort(a.created, false, true)}
                </span>
            )
        },
        {
            title: 'Updated By',
            render: (a: ApplicationSession) => (
                <span data-id-name="UpdatedBy">{a.updatedBy ? store.allUsers[a.updatedBy] || 'System' : ''}</span>
            )
        },
        {
            key: 'updated',
            title: 'Updated',
            sorter: true,
            sortOrder: getSorterOrder('updated'),
            // eslint-disable-next-line max-len
            render: (a: ApplicationSession) => (
                <span data-timestamp={Utils.dateStringToIso(formatDate(a.updated))} data-id-name="Updated">
                    {Utils.formatDateStringShort(a.updated, false, true)}
                </span>
            )
        },
        {
            title: 'Status',
            render: (a: ApplicationSession) => generateStatusIcon(a),
            width: '95px'
        },
        {
            render: (a: ApplicationSession) => generateActionButtons(a),
            width: '95px'
        }
    ];

    const updateFilter = React.useCallback(
        (filter: Partial<SessionFilter>) => {
            store.setArchiveSessionsParams({
                ...store.archiveSessionsParams,
                filter: {
                    ...store.archiveSessionsParams.filter,
                    ...filter,
                    page: filter.page ?? 0,
                    pageSize: filter.pageSize ?? store.archiveSessionsParams.filter.pageSize
                }
            });
        },
        [store]
    );

    const updateFilterAndLoadSessions = React.useCallback(
        (filter: Partial<SessionFilter>) => {
            updateFilter(filter);
            store.loadArchiveSessions();
        },
        [store, updateFilter]
    );

    const debouncedSearch = React.useMemo(() => debounce(() => store.loadArchiveSessions(), 500), [store]);

    React.useEffect(() => {
        return () => debouncedSearch.cancel();
    }, [debouncedSearch]);

    const pagination: TablePaginationConfig = {
        pageSizeOptions: ['10', '15', '20', '50', '100'],
        pageSize: store.archiveSessionsParams.filter.pageSize,
        total: store.archiveSessionsParams.total,
        current: store.archiveSessionsParams.filter.page + 1
    };

    const handleTableChange = (
        tablePagination: TablePaginationConfig,
        _: Record<string, React.ReactText[] | null>,
        sorter: SorterResult<ApplicationSession> | SorterResult<ApplicationSession>[]
    ) => {
        const singleSorter = Array.isArray(sorter) ? sorter[0] : sorter;
        const orderByField =
            isArchiveSortField(singleSorter.columnKey) && singleSorter.order ? singleSorter.columnKey : undefined;
        const orderByDirection =
            singleSorter.order === 'ascend' ? 'ASC' : singleSorter.order === 'descend' ? 'DESC' : undefined;

        updateFilterAndLoadSessions({
            page: (tablePagination.current ?? 1) - 1,
            pageSize: tablePagination.pageSize ?? store.archiveSessionsParams.filter.pageSize,
            orderByField,
            orderByDirection
        });
    };

    const handleClearArchiveFilters = React.useCallback(() => {
        debouncedSearch.cancel();
        store.clearArchiveFilters();
    }, [debouncedSearch, store]);

    const selectedUserId = store.archiveSessionsParams.filter.userId;

    return (
        <HasPermission permissionClaim={AppPermissions.CanAccessArchive}>
            <div className="sessions-list-header">
                <h1 className="header-title">Archive</h1>

                {!store.loadForCurrentUserOnly && store.hasAccessToAllEntities && (
                    <Select<{ value: string; label: React.ReactNode }>
                        className="user-select"
                        placeholder="Select user"
                        labelInValue
                        value={
                            selectedUserId
                                ? {
                                      value: selectedUserId,
                                      label: store.allUsers[selectedUserId] || selectedUserId
                                  }
                                : undefined
                        }
                        onDropdownVisibleChange={open => {
                            if (open) {
                                store.loadArchiveUserFilterOptions();
                            }
                        }}
                        onChange={(value?: { value: string }) => updateFilterAndLoadSessions({ userId: value?.value })}
                        options={store.allUsersList.map(user => ({
                            value: user.id,
                            label: user.name,
                            title: user.name
                        }))}
                        filterOption={(input, option) =>
                            option?.title && option.title.toLowerCase().includes(input.toLowerCase())
                        }
                        showSearch
                        allowClear
                        loading={store.archiveUserFilterOptionsLoading}
                        notFoundContent={
                            store.archiveUserFilterOptionsLoading ? (
                                <Spin size="small" />
                            ) : store.archiveUserFilterOptionsError ? (
                                <Button size="small" onClick={() => store.loadArchiveUserFilterOptions()}>
                                    Retry loading users
                                </Button>
                            ) : (
                                'No users'
                            )
                        }
                    />
                )}

                <DatePicker.RangePicker
                    className="date-picker"
                    value={getDatePickerValue()}
                    onChange={dateRange => {
                        let dateFrom: string | undefined;
                        let dateTo: string | undefined;

                        if (dateRange && dateRange[0] && dateRange[1]) {
                            dateFrom = mapPickerDateToUtcIso(dateRange[0], false);
                            dateTo = mapPickerDateToUtcIso(dateRange[1], true);
                        }

                        updateFilterAndLoadSessions({ dateFrom, dateTo });
                    }}
                />

                <Search
                    className="search"
                    placeholder="Search..."
                    value={store.archiveSessionsParams.filter.searchTerm ?? ''}
                    onChange={e => {
                        updateFilter({ searchTerm: e.target.value });
                        debouncedSearch();
                    }}
                    onSearch={value => {
                        debouncedSearch.cancel();
                        updateFilterAndLoadSessions({ searchTerm: value });
                    }}
                    enterButton
                    maxLength={150}
                    allowClear
                    prefix={<i className="alpha-icon xs search" style={{ marginRight: 10 }} />}
                />

                {store.archiveFiltersApplied && <Button onClick={handleClearArchiveFilters}>Clear filters</Button>}
            </div>
            <Card style={{ border: 'none' }} className="content-card table-content">
                <Observer>
                    {() => (
                        <Table
                            data-id="alpha-archive-table"
                            className="alpha-table no-border-top"
                            rowKey={r => r.id}
                            onRow={record => {
                                return {
                                    'data-id': record.id,
                                    id: record.id
                                };
                            }}
                            pagination={pagination}
                            columns={columns}
                            onChange={handleTableChange}
                            tableLayout="fixed"
                            dataSource={store.archiveSessions}
                            loading={store.archiveSessionsLoading}
                        />
                    )}
                </Observer>
            </Card>
        </HasPermission>
    );
};

export default observer(SessionsList);
